import { FinanceFinancialDocumentComplianceExtraFieldsConfig } from 'generated/resources/datatable.graphql'
import {
  ComplianceExtraFieldDefaultSettingFragment,
  ComplianceExtraFieldsConfigsFieldFragment,
  SaleInvoiceComplianceExtraFieldFragment,
  SaleInvoiceComplianceExtraFieldsFieldFragment,
  SaleInvoiceUpsertSidepanelQuery,
} from 'generated/resources/graphql'

import {
  FieldComplianceExtraField,
  FormComplianceExtraFields,
} from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/types'

type FinanceComplianceExtraFieldConfig = ComplianceExtraFieldsConfigsFieldFragment
type FinanceFinancialDocumentComplianceExtraFieldsConfigFromQuery = NonNullable<
  SaleInvoiceUpsertSidepanelQuery['finance']['financialDocumentComplianceExtraFieldsConfigs']
>[number]

export const countryComplianceExtraFieldsConfig = (
  complianceExtraFieldsConfigs: FinanceFinancialDocumentComplianceExtraFieldsConfig[],
  countryCode?: string
): FinanceFinancialDocumentComplianceExtraFieldsConfig | undefined => {
  return complianceExtraFieldsConfigs.filter((field) => field.countryCode === countryCode)?.[0]
}

export const buildSalesInvoiceFormComplianceExtraFieldsForNewCreditNote = (
  salesInvoiceComplianceExtraFields: SaleInvoiceComplianceExtraFieldFragment[],
  complianceExtraFieldsConfigs: FinanceFinancialDocumentComplianceExtraFieldsConfig[],
  countryCode?: string,
  defaultComplianceExtraFieldsSettingsForLegalEntity?: ComplianceExtraFieldDefaultSettingFragment | null
): FormComplianceExtraFields | null => {
  const saleInvoiceComplianceExtraFieldsBase = buildSalesInvoiceFormComplianceExtraFields(
    salesInvoiceComplianceExtraFields,
    complianceExtraFieldsConfigs,
    countryCode
  )
  // if we are creating a credit note from an invoice without any compliance extra fields
  // we can fill the fields with the defaults values
  if (!saleInvoiceComplianceExtraFieldsBase)
    return buildFormComplianceExtraFieldsWithDefaultValues(
      complianceExtraFieldsConfigs,
      true,
      countryCode,
      defaultComplianceExtraFieldsSettingsForLegalEntity
    )

  const complianceExtraFields = buildComplianceExtraFieldsForNewCreditNote(
    saleInvoiceComplianceExtraFieldsBase,
    complianceExtraFieldsConfigs,
    countryCode,
    defaultComplianceExtraFieldsSettingsForLegalEntity
  )

  return {
    ...saleInvoiceComplianceExtraFieldsBase,
    id: null,
    fields: complianceExtraFields,
  }
}

const buildComplianceExtraFieldsForNewCreditNote = (
  saleInvoiceComplianceExtraFieldsBase: FormComplianceExtraFields,
  complianceExtraFieldsConfigs: FinanceFinancialDocumentComplianceExtraFieldsConfig[],
  countryCode?: string,
  defaultComplianceExtraFieldsSettingsForLegalEntity?: ComplianceExtraFieldDefaultSettingFragment | null
): FieldComplianceExtraField[] => {
  const config = countryComplianceExtraFieldsConfig(complianceExtraFieldsConfigs, countryCode)
  if (!config?.fields) return []

  //for new credit notes, we want to keep the same info for compliance extra fields as the original invoice for document regime and for the line items
  //for those fields that do not exist in the invoice, we want to fill them with the default values
  const preservedFields = saleInvoiceComplianceExtraFieldsBase.fields.filter(
    (field) => field.name === 'es-verifactu-regime' || !!field.associatedFieldId
  )

  return config.fields.flatMap((configField) => {
    const existingFields = preservedFields.filter((field) => field.name === configField.name)

    if (existingFields.length > 0) return existingFields

    const defaultValue = getDefaultValueForComplianceExtraField(
      true,
      configField,
      defaultComplianceExtraFieldsSettingsForLegalEntity
    )
    return [
      buildComplianceExtraField(
        configField.name,
        defaultValue,
        configField.mandatoryForCreditNote,
        configField.mandatoryForStandardInvoice
      ),
    ]
  })
}

export const buildSalesInvoiceFormComplianceExtraFields = (
  salesInvoiceComplianceExtraFields: SaleInvoiceComplianceExtraFieldFragment[],
  complianceExtraFieldsConfigs: FinanceFinancialDocumentComplianceExtraFieldsConfig[],
  countryCode?: string
): FormComplianceExtraFields | null => {
  const config = countryComplianceExtraFieldsConfig(complianceExtraFieldsConfigs, countryCode)
  if (!config) return null

  const countryComplianceExtraField = salesInvoiceComplianceExtraFields.filter(
    (field) => field.countryCode === countryCode
  )?.[0]

  if (!countryComplianceExtraField) return null

  const fieldsWithRequiredRule = countryComplianceExtraField.fields.map((field) => {
    const configField = config.fields.find((fieldConfig) => fieldConfig.name === field.name)
    return buildComplianceExtraField(
      field.name,
      field.value,
      configField?.mandatoryForCreditNote ?? false,
      configField?.mandatoryForStandardInvoice ?? false,
      field.associatedFieldId
    )
  })

  return {
    ...countryComplianceExtraField,
    fields: fieldsWithRequiredRule,
  }
}

export const buildFormComplianceExtraFieldsWithDefaultValues = (
  complianceExtraFieldsConfigs: FinanceFinancialDocumentComplianceExtraFieldsConfig[],
  isCreditNote: boolean,
  countryCode?: string,
  defaultComplianceExtraFieldsSettingsForLegalEntity?: ComplianceExtraFieldDefaultSettingFragment | null
): FormComplianceExtraFields | null => {
  const config = countryComplianceExtraFieldsConfig(complianceExtraFieldsConfigs, countryCode)
  if (!config) return null

  return {
    id: null,
    countryCode: config.countryCode,
    organization: config.organization,
    fields: buildComplianceExtraFieldsWithDefaultValues(
      config,
      'document',
      isCreditNote,
      undefined,
      defaultComplianceExtraFieldsSettingsForLegalEntity
    ),
  }
}

export const buildComplianceExtraFieldsWithDefaultValues = (
  config: FinanceFinancialDocumentComplianceExtraFieldsConfig,
  context: 'document' | 'line_item',
  isCreditNote: boolean,
  associatedFieldId?: string,
  defaultComplianceExtraFieldsSettingsForLegalEntity?: ComplianceExtraFieldDefaultSettingFragment | null
) => {
  return config.fields
    .filter((el) => el.fieldContext === context)
    .map((field) => {
      const defaultValue = getDefaultValueForComplianceExtraField(
        isCreditNote,
        field,
        defaultComplianceExtraFieldsSettingsForLegalEntity
      )
      return buildComplianceExtraField(
        field.name,
        defaultValue,
        field.mandatoryForCreditNote,
        field.mandatoryForStandardInvoice,
        associatedFieldId
      )
    })
}

const getDefaultValueForComplianceExtraField = (
  isCreditNote: boolean,
  configField: FinanceComplianceExtraFieldConfig,
  defaultComplianceExtraFieldsSettingsForLegalEntity?: ComplianceExtraFieldDefaultSettingFragment | null
) => {
  const defaultComplianceExtraFieldsSettingValues = isCreditNote
    ? defaultComplianceExtraFieldsSettingsForLegalEntity?.creditNoteDefaultValues
    : defaultComplianceExtraFieldsSettingsForLegalEntity?.invoiceDefaultValues

  const defaultValueFromSettings = defaultComplianceExtraFieldsSettingValues?.find(
    (el) => el.fieldName === configField.name
  )?.value

  return defaultValueFromSettings
    ? defaultValueFromSettings
    : isCreditNote
      ? configField.defaultValueForCreditNote
      : configField.defaultValueForStandardInvoice
}

export const complianceExtraFieldLabelForValue = (
  complianceExtraFieldsConfig: FinanceComplianceExtraFieldConfig,
  value: string
) => {
  if (!complianceExtraFieldsConfig.allowedValues) return value
  const found = complianceExtraFieldsConfig.allowedValues.find((v) => v.value === value)
  return found ? found.label : value
}

export const sortedComplianceExtraFieldsForContext = (
  complianceExtraFieldsConfigs: FinanceComplianceExtraFieldConfig[],
  context: 'document' | 'line_item'
) => {
  return [...complianceExtraFieldsConfigs]
    .sort((a, b) => a.order - b.order)
    .filter((field) => field.fieldContext === context)
}

export const complianceExtraFieldsForLineItemId = (
  complianceExtraFieldsConfigs: FinanceComplianceExtraFieldConfig[],
  fields: SaleInvoiceComplianceExtraFieldsFieldFragment[],
  lineItemId: string
): SaleInvoiceComplianceExtraFieldsFieldFragment[] => {
  const lineItemFields = fields.filter((field) => field.associatedFieldId === lineItemId) || []

  return complianceExtraFieldsConfigs.map((config) => {
    const configLineItem = lineItemFields.find((field) => field.name === config.name)
    return configLineItem
      ? {
          ...configLineItem,
          value: complianceExtraFieldLabelForValue(config, configLineItem?.value || '-'),
        }
      : {
          name: config.name,
          value: '-',
          associatedFieldId: lineItemId,
        }
  })
}

export const findDefaultComplianceExtraFieldsSettingsForLegalEntity = (
  defaultComplianceExtraFieldsSettings: ComplianceExtraFieldDefaultSettingFragment[],
  legalEntityId: string
): ComplianceExtraFieldDefaultSettingFragment | undefined => {
  return defaultComplianceExtraFieldsSettings?.find(
    (setting) => setting.legalEntity?.id === legalEntityId
  )
}

const buildComplianceExtraField = (
  name: string,
  value: string | null | undefined,
  mandatoryForCreditNote: boolean,
  mandatoryForStandardInvoice: boolean,
  associatedFieldId?: string | null | undefined
): FieldComplianceExtraField => {
  return {
    name,
    value,
    mandatoryForCreditNote,
    mandatoryForStandardInvoice,
    associatedFieldId,
  }
}
